import { usePersistedFilters } from '@/hooks/usePersistedFilters';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  ExpandMore,
  CheckCircle,
  Cancel,
  Warning,
  ArrowForward,
  OpenInNew,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { processMapService, ProcessMapTransition, ProcessMapState } from '@/services/core/ProcessMapService';
import { stateMachineService } from '@/services/core/StateMachineService';

// ─── State box ───────────────────────────────────────────────────────────────

function StateBox({ state }: { state: ProcessMapState }) {
  const { t } = useTranslation();
  const color = state.isInitial ? 'primary' : state.isTerminal ? 'default' : 'default';
  const variant = state.isInitial ? 'filled' : state.isTerminal ? 'outlined' : 'outlined';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
      <Chip
        label={state.name}
        color={color}
        variant={variant}
        size="medium"
        sx={{
          fontWeight: 600,
          fontSize: '0.8rem',
          px: 1,
          opacity: state.isTerminal ? 0.65 : 1,
          borderStyle: state.isTerminal ? 'dashed' : 'solid',
        }}
      />
      {state.isInitial && (
        <Typography variant="caption" color="text.secondary">{t('processMap.initial')}</Typography>
      )}
      {state.isTerminal && (
        <Typography variant="caption" color="text.secondary">{t('processMap.terminal')}</Typography>
      )}
    </Box>
  );
}

// ─── Transition card ──────────────────────────────────────────────────────────

function TransitionCard({ transition }: { transition: ProcessMapTransition }) {
  const { t } = useTranslation();
  const isGated = transition.approvalGate.isGated;
  const hasEnabledSubscription = transition.eventChains.some(c =>
    c.workflows.some(w => w.subscriptionEnabled)
  );
  const hasDisabledSubscription = transition.eventChains.some(c =>
    c.workflows.some(w => !w.subscriptionEnabled && w.workflowName)
  );

  const borderColor = isGated ? 'warning.main'
    : hasEnabledSubscription ? 'success.main'
    : hasDisabledSubscription ? 'text.disabled'
    : 'divider';

  return (
    <Accordion
      disableGutters
      sx={{
        border: 1,
        borderColor,
        borderRadius: 1,
        '&:before': { display: 'none' },
        mb: 1,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2, py: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, flexWrap: 'wrap', gap: 1 }}>
          {/* From → To */}
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Chip label={transition.fromState} size="small" variant="outlined" />
            <ArrowForward fontSize="small" sx={{ color: 'text.secondary' }} />
            <Chip label={transition.toState} size="small" variant="outlined" />
          </Stack>

          {/* Transition key */}
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            {transition.key}
          </Typography>

          {/* Badges */}
          <Stack direction="row" spacing={0.5} sx={{ ml: 'auto' }}>
            {isGated && (
              <Tooltip title={t('processMap.approvalGatedTooltip', { policy: transition.approvalGate.policyName ?? t('processMap.policyConfigured') })}>
                <Chip icon={<Warning fontSize="small" />} label={t('processMap.approval')} size="small"
                  color="warning" variant="outlined" />
              </Tooltip>
            )}
            {transition.rules.length > 0 && (
              <Chip label={t('processMap.ruleCount', { count: transition.rules.length })}
                size="small" variant="outlined" />
            )}
            {transition.eventChains.length > 0 && (
              <Chip label={t('processMap.eventCount', { count: transition.eventChains.length })}
                size="small" variant="outlined" color="info" />
            )}
          </Stack>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
        <Stack spacing={2}>
          {/* Permission */}
          {transition.permission && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {t('processMap.permission')}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.25 }}>
                {transition.permission}
              </Typography>
            </Box>
          )}

          {/* Rules */}
          {transition.rules.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {t('processMap.businessRules')}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75} mt={0.5}>
                {transition.rules.map(r => (
                  <Chip
                    key={r.ruleKey}
                    label={r.displayName ?? r.ruleKey}
                    size="small"
                    color={r.isDraft ? 'warning' : r.isEnabled ? 'success' : 'default'}
                    variant={r.isEnabled ? 'filled' : 'outlined'}
                    title={r.ruleKey}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Event chains */}
          {transition.eventChains.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {t('processMap.eventsAndWorkflows')}
              </Typography>
              <Stack spacing={1} mt={0.5}>
                {transition.eventChains.map(chain => (
                  <Box key={chain.eventFullType}
                    sx={{ pl: 1, borderLeft: 2, borderColor: 'divider' }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {chain.routingDecision && (
                        <Chip label={chain.routingDecision} size="small" variant="outlined"
                          color={chain.routingDecision === 'APPROVAL_REQUIRED' ? 'warning' : 'success'}
                          sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
                      )}
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {chain.eventName}
                      </Typography>
                    </Stack>

                    {chain.workflows.length > 0 && (
                      <Stack spacing={0.5} mt={0.5} pl={1}>
                        {chain.workflows.map(wf => (
                          <Stack key={wf.workflowName} direction="row" alignItems="center" spacing={1}>
                            {wf.subscriptionEnabled
                              ? <CheckCircle fontSize="small" sx={{ color: 'success.main' }} />
                              : <Cancel fontSize="small" sx={{ color: 'text.disabled' }} />
                            }
                            <Typography variant="body2" color={wf.subscriptionEnabled ? 'text.primary' : 'text.disabled'}>
                              {wf.workflowName}
                            </Typography>
                            {wf.hasApprovalGate && (
                              <Chip label="approvalGate" size="small" color="warning" variant="outlined"
                                sx={{ fontSize: '0.65rem' }} />
                            )}
                          </Stack>
                        ))}
                      </Stack>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ProcessMapPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filterState, setFilterState] = usePersistedFilters('processMap', { entityType: '' });
  const selectedEntity = filterState.entityType;
  const setSelectedEntity = (value: string) => setFilterState(prev => ({ ...prev, entityType: value }));

  // The list endpoint enumerates (entity, machine) pairs; the picker is per entity.
  const { data: stateMachineSummaries = [], isLoading: loadingEntities } = useQuery({
    queryKey: ['state-machine-entity-types'],
    queryFn: () => stateMachineService.getAvailableStateMachines(),
  });
  const entityTypes = [...new Set(stateMachineSummaries.map((s) => s.entityName))];

  const { data: processMap, isLoading: loadingMap, error } = useQuery({
    queryKey: ['process-map', selectedEntity],
    queryFn: () => processMapService.getProcessMap(selectedEntity),
    enabled: !!selectedEntity,
  });

  return (
    <Box sx={{ p: 3, maxWidth: 1100 }}>
      {/* Header */}
      <Typography variant="h5" fontWeight={600} gutterBottom>
        {t('processMap.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('processMap.subtitle')}
      </Typography>

      {/* Entity selector */}
      <FormControl size="small" sx={{ minWidth: 240, mb: 4 }}>
        <InputLabel>{t('processMap.entityType')}</InputLabel>
        <Select
          label={t('processMap.entityType')}
          value={selectedEntity}
          onChange={e => setSelectedEntity(e.target.value)}
          disabled={loadingEntities}
        >
          {entityTypes.map(e => (
            <MenuItem key={e} value={e}>{e}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {loadingMap && <CircularProgress size={24} />}
      {error && <Alert severity="error">{String(error)}</Alert>}

      {processMap && (
        <Stack spacing={4}>
          {/* ── One graph per state machine (the primary first); a single-machine entity shows no
                 machine caption. The caption is the bound field's display name. ── */}
          {processMap.stateMachines.map((machine, machineIndex) => (
          <Stack key={machine.stateMachine} spacing={4}>
          {machineIndex > 0 && <Divider />}
          {processMap.stateMachines.length > 1 && (
            <Typography variant="h6" fontWeight={600}>
              {t(`DisplayName:${processMap.entityType}.${machine.stateMachine}`, { defaultValue: machine.stateMachine })}
            </Typography>
          )}

          {/* ── States overview ── */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {t('processMap.states')}
              <Chip label={processMap.module} size="small" sx={{ ml: 1 }} variant="outlined" />
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={2} alignItems="flex-start">
              {machine.states.map(s => (
                <StateBox key={s.name} state={s} />
              ))}
            </Stack>
          </Box>

          <Divider />

          {/* ── Transitions ── */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {t('processMap.transitions')}
            </Typography>

            {/* Legend */}
            <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'warning.main' }} />
                <Typography variant="caption">{t('processMap.approvalGated')}</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CheckCircle fontSize="small" sx={{ color: 'success.main', fontSize: 14 }} />
                <Typography variant="caption">{t('processMap.subscriptionEnabled')}</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Cancel fontSize="small" sx={{ color: 'text.disabled', fontSize: 14 }} />
                <Typography variant="caption">{t('processMap.subscriptionDisabled')}</Typography>
              </Stack>
            </Stack>

            {machine.transitions.map(tr => (
              <TransitionCard key={tr.key} transition={tr} />
            ))}
          </Box>
          </Stack>
          ))}

          <Divider />

          {/* ── Approval Policies ── */}
          {processMap.approvalPolicies.length > 0 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t('processMap.approvalPolicies')}
                <Chip
                  label={t('processMap.activeCount', { count: processMap.approvalPolicies.filter(p => p.isEnabled).length })}
                  size="small"
                  color="warning"
                  sx={{ ml: 1 }}
                />
              </Typography>
              <Stack spacing={1}>
                {processMap.approvalPolicies.map(p => (
                  <Card key={p.id} variant="outlined"
                    sx={{ borderColor: p.isEnabled ? 'warning.main' : 'divider' }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" alignItems="center" spacing={1} mb={p.steps.length > 0 ? 1 : 0}>
                        <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                        <Chip
                          label={p.isEnabled ? t('common.active') : t('common.inactive')}
                          size="small"
                          color={p.isEnabled ? 'warning' : 'default'}
                          variant={p.isEnabled ? 'filled' : 'outlined'}
                        />
                        <Chip label={t('processMap.priority', { value: p.priority })} size="small" variant="outlined" />
                        <IconButton
                          size="small"
                          sx={{ ml: 'auto' }}
                          onClick={() => navigate(`/workflow/approvalpolicies/${p.id}`)}
                          title={t('processMap.openPolicy')}
                        >
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Stack>

                      {p.steps.length > 0 && (
                        <Stack spacing={0.5}>
                          {p.steps.map((step, i) => (
                            <Box key={i} sx={{ pl: 1, borderLeft: 2, borderColor: 'warning.light' }}>
                              <Stack direction="row" flexWrap="wrap" gap={0.5} alignItems="center">
                                <Typography variant="caption" color="text.secondary">
                                  {step.name ? t('processMap.stepNamed', { number: i + 1, name: step.name }) : t('processMap.step', { number: i + 1 })}
                                </Typography>
                                {step.approvalPattern && (
                                  <Chip label={step.approvalPattern} size="small" variant="outlined"
                                    sx={{ fontSize: '0.65rem' }} />
                                )}
                                {step.assignmentStrategy && (
                                  <Chip label={step.assignmentStrategy} size="small" variant="outlined"
                                    sx={{ fontSize: '0.65rem' }} />
                                )}
                                {step.requiredPermission && (
                                  <Chip label={step.requiredPermission} size="small" variant="outlined"
                                    sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }} />
                                )}
                                {step.timeoutDuration && (
                                  <Chip
                                    label={t('processMap.timeout', { duration: step.timeoutDuration, action: step.timeoutAction ?? '?' })}
                                    size="small"
                                    variant="outlined"
                                    color="default"
                                    sx={{ fontSize: '0.65rem' }}
                                  />
                                )}
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}

          {/* ── Workflow Subscriptions ── */}
          {processMap.workflowSubscriptions.length > 0 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t('processMap.workflowSubscriptions')}
                <Chip
                  label={t('processMap.enabledCount', { count: processMap.workflowSubscriptions.filter(s => s.isEnabled).length })}
                  size="small"
                  color="success"
                  sx={{ ml: 1 }}
                />
              </Typography>
              <Stack spacing={0.75}>
                {processMap.workflowSubscriptions.map(s => (
                  <Card key={s.id} variant="outlined"
                    sx={{ borderColor: s.isEnabled ? 'success.main' : 'divider' }}>
                    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                        {s.isEnabled
                          ? <CheckCircle fontSize="small" sx={{ color: 'success.main' }} />
                          : <Cancel fontSize="small" sx={{ color: 'text.disabled' }} />
                        }
                        <Typography variant="body2" fontWeight={500}
                          color={s.isEnabled ? 'text.primary' : 'text.disabled'}>
                          {s.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}>
                          {s.eventName}
                        </Typography>
                        <ArrowForward fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="caption">{s.workflowDefinitionId}</Typography>
                        <Chip label={s.mode} size="small" variant="outlined"
                          sx={{ ml: 'auto', fontSize: '0.65rem' }} />
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/core/workflow-chaining/subscriptions/${s.id}`)}
                          title={t('processMap.openSubscription')}
                        >
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}

          {/* Empty states */}
          {processMap.approvalPolicies.length === 0 && processMap.workflowSubscriptions.length === 0 && (
            <Alert severity="info">
              {t('processMap.noConfig', { entity: processMap.entityType })}
            </Alert>
          )}
        </Stack>
      )}
    </Box>
  );
}
